import { isTokenExpired } from '@/utils/auth';
import axios from 'axios';
import { handleLogout, type Response } from './axiosInstance';

const paymentInstance = axios.create({
    baseURL: import.meta.env.VITE_PAYMENT_CENTER_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

paymentInstance.interceptors.request.use(
    async config => {
        if (config.headers && config.headers['Skip-Auth']) {
            delete config.headers['Skip-Auth'];
            return config;
        }
        const token = localStorage.getItem('token');
        if (token) {
            if (isTokenExpired(token)) {
                handleLogout();
                window.location.href = (import.meta.env.VITE_ROUTE_BASE ?? "") + '/login';
            } else {
                config.headers.Authorization = `Bearer ${token}`;
            }
        } else {
            window.location.href = (import.meta.env.VITE_ROUTE_BASE ?? "") + '/login';
        }
        return config;
    },
    error => {
        return Promise.reject(error);
    }
);

export type Product = {
    id: number;
    name: string;
    description: string;
    amount: number;
    currency: string;
    target_role: string;
    is_active: boolean;
    created_at: string;
    updated_at: string;
};

export type PaymentRecord = {
    id: number;
    payment_intent_id: string;
    merchant_order_id: string;
    user_email: string;
    product_id: number;
    amount: number;
    currency: string;
    status: string;
    created_at: string;
    updated_at: string;
};

export type CreatePaymentResponse = {
    payment_id: number;
    payment_intent_id: string;
    client_secret: string;
    amount: number;
    currency: string;
    status: string;
};

export const getProducts = async (): Promise<Response<Product[]> | undefined> => {
    try {
        const response = await paymentInstance.get<Response<Product[]>>('/api/v1/products', {
            headers: { 'Skip-Auth': true }
        });
        return response?.data;
    } catch (e) {
        console.error('Failed to fetch products', e);
        return undefined;
    }
};

export const createPayment = async (productId: number): Promise<Response<CreatePaymentResponse> | undefined> => {
    try {
        const response = await paymentInstance.post<Response<CreatePaymentResponse>>('/api/v1/payments/create', {
            product_id: productId
        });
        return response?.data;
    } catch (e) {
        console.error('Failed to create payment', e);
        return undefined;
    }
};

export const getPaymentDetail = async (id: number): Promise<Response<PaymentRecord> | undefined> => {
    try {
        const response = await paymentInstance.get<Response<PaymentRecord>>(`/api/v1/payments/${id}`);
        return response?.data;
    } catch (e) {
        console.error('Failed to fetch payment detail', e);
        return undefined;
    }
};

export const getMyPayments = async (): Promise<Response<PaymentRecord[]> | undefined> => {
    try {
        const response = await paymentInstance.get<Response<PaymentRecord[]>>('/api/v1/payments/me');
        return response?.data;
    } catch (e) {
        console.error('Failed to fetch payment history', e);
        return undefined;
    }
};
